import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { GridRow, GridToolbar, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid-pro";
import { Snackbar, CircularProgress, Grid, Tooltip, Button } from "@mui/material";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import PrintIcon from "@mui/icons-material/Print";
import { CreateMaterialItemDialog } from "./dialogs/CreateMaterialItemDialog";
import Alert from "@mui/material/Alert";
import { decoratedFetch } from "../req_utils";
import { GENESIS_LOGO_COLOR } from "../constants";
import { useSelector, useDispatch } from "react-redux";
import { updateSelectedProposal, updateProjectKeepClean } from "../reducers";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { styled } from "@mui/material/styles";
import "../styles.css";
import ThemedDataGridPro from "./ThemedDataGridPro";
import ThemedIconButton from "./ThemedIconButton";

const CustomRow = styled(GridRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CustomRowWithTooltip = (props) => {
  const { row, ...other } = props;
  return (
    <Tooltip title={row.notes || ""} arrow>
      <div>
        <CustomRow {...other} />
      </div>
    </Tooltip>
  );
};

const CustomToolbar = ({ materialItems, projectName, apiRef }) => {
  const handleCustomPrint = () => {
    // Get visible columns from the DataGrid API
    const visibleColumns = apiRef.current?.getVisibleColumns() || [];
    const visibleColumnFields = visibleColumns
      .filter(col => col.field !== 'actions') // Always exclude actions column
      .map(col => col.field);

    // Create a flattened version of all material items for printing
    const flattenedItems = materialItems.map(item => ({
      ...item,
      // Add indentation for subitems to show hierarchy
      material_type: item.main_item_id ? `  ${item.material_type}` : item.material_type,
      // Fix unit display - handle both string and object cases
      unit_display: typeof item.size_unit === 'object' ? item.size_unit?.name || '' : item.size_unit || '',
    }));

    // Define column mapping for print headers
    const columnHeaders = {
      material_type: 'Type',
      material_vendor: 'Manufacturer',
      material_style: 'Product',
      material_color: 'Color',
      material_size: 'Size',
      quantity: 'Quantity',
      size_unit: 'Unit',
      unit_price: 'Unit Price',
      total_price: 'Total $'
    };

    // Filter to only visible columns
    const visibleHeaders = visibleColumnFields
      .filter(field => columnHeaders[field])
      .map(field => ({ field, header: columnHeaders[field] }));

    // Create a temporary window for printing
    const printWindow = window.open('', '_blank');
    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Materials Print - ${projectName}</title>
          <style>
            @page {
              margin: 20mm 30mm 20mm 20mm;
              @top-left { content: ""; }
              @top-center { content: ""; }
              @bottom-right { content: ""; }
            }
            @media print {
              body { 
                transform: scale(0.75); 
                transform-origin: top left; 
                width: 133.33%; 
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .header {
                background-color: #002466 !important;
                color: white !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              th {
                background-color: #002466 !important;
                color: white !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              tr:nth-child(even) {
                background-color: #f8f9fa !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            body {
              font-family: Arial, sans-serif;
              color: #002466;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding: 15px;
              background-color: #002466;
              color: white;
              border-radius: 8px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: bold;
            }
            .header h2 {
              margin: 5px 0 0 0;
              font-size: 18px;
              font-weight: normal;
              opacity: 0.9;
            }
            .header .date-time {
              margin-top: 8px;
              font-size: 14px;
              opacity: 0.8;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              font-family: Arial, sans-serif; 
              margin: 0 50px 0 0;
            }
            th, td { 
              border: 1px solid #001843; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #002466; 
              color: white;
              font-weight: bold; 
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            th:last-child, td:last-child { width: 60px; padding-right: 20px; }
            tr:nth-child(even) {
              background-color: #f8f9fa;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Genesis Flooring</h1>
            <h2>Materials List - ${projectName}</h2>
            <div class="date-time">Generated on ${currentDate} at ${currentTime}</div>
          </div>
          <table>
            <thead>
              <tr>
                ${visibleHeaders.map(col => `<th>${col.header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${flattenedItems.map(item => `
                <tr>
                  ${visibleHeaders.map(col => {
                    if (col.field === 'size_unit') {
                      return `<td>${item.unit_display}</td>`;
                    }
                    return `<td>${item[col.field] || ''}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <GridToolbarContainer>
      <GridToolbar />
      <Button
        onClick={handleCustomPrint}
        startIcon={<PrintIcon />}
        variant="text"
        sx={{
          color: '#1976d2',
          textTransform: 'none',
          minWidth: 'auto',
          padding: '4px',
          mt: '3px',
          marginLeft: '-8px',
          fontSize: '0.8125rem',
          fontWeight: 500,
          '& .MuiButton-startIcon': {
            marginRight: '6px',
            marginLeft: '-2px',
          },
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
          }
        }}
      >
        PRINT
      </Button>
    </GridToolbarContainer>
  );
};

const MaterialsTable = React.memo(
  ({
    gridKey,
    rows,
    columns,
    loading,
    getTreeDataPath,
    handleRowSelectionModelChange,
    apiRef,
    projectName,
  }) => {
    const CustomToolbarWithData = () => <CustomToolbar materialItems={rows} projectName={projectName} apiRef={apiRef} />;
    
    return (
      <ThemedDataGridPro
        key={gridKey}
        rows={rows}
        columns={columns}
        loading={loading}
        slots={{ toolbar: CustomToolbarWithData }}
        treeData
        getTreeDataPath={getTreeDataPath}
        pageSize={5}
        rowsPerPageOptions={[5, 10, 25, 50]}
        checkboxSelection
        onRowSelectionModelChange={(newSelectionModel) =>
          handleRowSelectionModelChange(newSelectionModel)
        }
        getRowId={(row) => row.id}
        onCellClick={(params, e) => e.stopPropagation()}
        localeText={{ noRowsLabel: "No materials" }}
        groupingColDef={{
          headerName: "",
          width: 300,
          flex: 1,
        }}
        sx={{
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cellCheckbox:focus, & .MuiDataGrid-cellCheckbox:focus-within":
            {
              outline: "none",
            },
          "& .MuiDataGrid-toolbarContainer button[aria-label*='Export']": {
            display: "none !important",
          },
        }}
        components={{
          Row: CustomRowWithTooltip,
        }}
        getRowClassName={(params) =>
          params.row.main_item_id ? "nested-row" : ""
        }
        apiRef={apiRef}
      />
    );
  },
);

function MaterialItems({ value, index }) {
  const dispatchState = useDispatch();
  const { project_id } = useParams();
  const project = useSelector((state) => state.project);
  const selectedProposal = useSelector((state) => state.selectedProposal);
  const [new_main_item_id, setNewMainItemId] = useState("");
  const [materialItems, setMaterialItems] = useState([]);
  const [selectedMaterialItems, setSelectedMaterialItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [gridKey, setGridKey] = useState(false);
  const [editableMaterialItem, setEditableMaterialItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [isExistingProposal, setIsExistingProposal] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const apiRef = useRef(null);

  const columns = [
    {
      field: "material_type",
      headerName: "Type",
      flex: 3,
    },
    {
      field: "material_vendor",
      headerName: "Manufacturer",
      flex: 4,
    },
    {
      field: "material_style",
      headerName: "Product",
      flex: 4,
    },
    {
      field: "material_color",
      headerName: "Color",
      flex: 3,
    },
    {
      field: "material_size",
      headerName: "Size",
      flex: 2,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 2,
    },
    {
      field: "size_unit",
      headerName: "Unit",
      valueGetter: (sizeUnit) => sizeUnit.name,
      flex: 2,
    },
    {
      field: "unit_price",
      headerName: "Unit Price",
      flex: 2,
    },
    {
      field: "total_price",
      headerName: "Total $",
      flex: 2,
    },
    {
      field: "actions",
      headerName: "",
      renderCell: (params) => (
        <div>
          <ThemedIconButton
            onClick={() => {
              const editableItem = materialItems.filter(
                (materialItem) => materialItem.id === params.row.id,
              )[0];
              setEditableMaterialItem(editableItem);
              setOpen(true);
            }}
          >
            <EditIcon />
          </ThemedIconButton>
          <ThemedIconButton
            sx={{ marginLeft: "10px" }}
            onClick={() => {
              setNewMainItemId(
                materialItems.filter(
                  (materialItem) => materialItem.id === params.row.id,
                )[0].id,
              );
              setOpen(true);
            }}
          >
            <PlaylistAddIcon />
          </ThemedIconButton>
        </div>
      ),
      width: 110,
    },
  ];

  const handleRowSelectionModelChange = useCallback(
    (newSelectionModel) => {
      setSelectedMaterialItems(newSelectionModel);
    },
    [setSelectedMaterialItems],
  );

  const handleDialogClose = () => {
    setSelectedMaterialItems([]);
    setOpen(false);
    setEditableMaterialItem(null);
    setNewMainItemId("");
    setGridKey(!gridKey); // Force re-render
  };

  const listProjectMaterialItems = (isProposal) => {
    setMaterialsLoading(true);
    decoratedFetch(
      `/list_project_material_items?${isProposal ? "proposal_id" : "project_id"}=${isProposal ? selectedProposal.id : project_id}`,
    )
      .then((response) => response.json())
      .then((data) => {
        if (data && data.length > 0) {
          setMaterialItems(data);
        } else {
          setMaterialItems([]);
        }
        setMaterialsLoading(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Error listing project materials: ${error}`);
        setLoading(false);
        setMaterialsLoading(false);
        setMaterialItems([]);
      });
  };

  const updateProjectMaterialSubtotal = (itemTotalDiff) => {
    dispatchState(
      updateProjectKeepClean({
        ...project,
        material_subtotal: project.material_subtotal + itemTotalDiff,
      }),
    );
  };

  const updateProposalMaterialSubtotal = (itemTotalDiff) => {
    dispatchState(
      updateSelectedProposal({
        material_subtotal: selectedProposal.material_subtotal + itemTotalDiff,
      }),
    );
  };

  useEffect(() => {
    if (value !== index) {
      return;
    }
    const isProposal =
      selectedProposal &&
      Object.keys(selectedProposal).length !== 0 &&
      selectedProposal.id !== "new_proposal";
    listProjectMaterialItems(isProposal);
    setIsExistingProposal(isProposal);
  }, [value, selectedProposal]);

  const handleDialogSubmit = (totalPrice, prevTotalPrice, isProposal) => {
    if (isProposal) {
      updateProposalMaterialSubtotal(totalPrice - prevTotalPrice);
    } else {
      updateProjectMaterialSubtotal(totalPrice - prevTotalPrice);
    }
    listProjectMaterialItems(isProposal);
  };

  const findAllDescendants = (ids) => {
    const allIds = new Set(ids);
    let idsToProcess = ids;

    while (idsToProcess.length) {
      let newIds = [];
      idsToProcess.forEach((id) => {
        materialItems.forEach((item) => {
          if (item.main_item_id === id && !allIds.has(item.id)) {
            allIds.add(item.id);
            newIds.push(item.id);
          }
        });
      });
      idsToProcess = newIds;
    }

    return Array.from(allIds);
  };

  const handleRemoveMaterialItems = () => {
    setLoading(true);
    const itemsToDelete = findAllDescendants(selectedMaterialItems);
    decoratedFetch(`/delete_project_material_items`, {
      method: "POST",
      body: JSON.stringify({
        material_item_ids: itemsToDelete,
        project_id: project.id,
        proposal_id: isExistingProposal ? selectedProposal.id : "",
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        // Calculate the total price of deleted items to update the subtotal
        const selectedMaterialItemTotalPrices = materialItems
          .filter((item) => itemsToDelete.includes(item.id))
          .map((item) => parseFloat(item.total_price));
        // Filter out deleted items from the local state
        setMaterialItems(
          materialItems.filter((item) => !itemsToDelete.includes(item.id)),
        );
        const positiveDiff = selectedMaterialItemTotalPrices.reduce(
          (sum, totalPrice) => sum + totalPrice,
          0,
        );
        if (isExistingProposal) {
          updateProposalMaterialSubtotal(-1 * positiveDiff);
        } else {
          updateProjectMaterialSubtotal(-1 * positiveDiff);
        }
        setLoading(false);
        setSnackbarOpen(true);
        setSnackbarMessage(
          `Material${itemsToDelete.length > 1 ? "s" : ""} deleted`,
        );
        setSelectedMaterialItems([]);
      })
      .catch((error) => {
        setLoading(false);
        console.error(`Error deleting project material: ${error}`);
        setSnackbarOpen(true);
        setSnackbarMessage(
          `An error occurred when deleting material${selectedMaterialItems.length > 1 ? "s" : ""}`,
        );
      });
  };

  const getTreeDataPath = useCallback(
    (data) => {
      const path = [];
      let current = data;
      while (current) {
        path.push(current.id); // Append the current item's id to the path
        if (!current.main_item_id) break; // If there's no parent, break the loop
        current = materialItems.find(
          (item) => item.id === current.main_item_id,
        ); // Move to the parent
      }
      return path.reverse();
    },
    [materialItems],
  );

  const handleToggleExpandAll = () => {
    if (!apiRef.current) return;
    
    // Find all parent items (items that have children)
    const parentIds = materialItems
      .filter((item) => !item.main_item_id)
      .filter((item) => 
        materialItems.some((child) => child.main_item_id === item.id)
      )
      .map((item) => item.id);
    
    // Toggle expansion for all parent items
    parentIds.forEach((id) => {
      apiRef.current.setRowChildrenExpansion(id, !allExpanded);
    });
    
    setAllExpanded(!allExpanded);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage("");
  };

  return (
    <>
      <Snackbar
        sx={{
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
      {alertMessage ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Alert
            onClose={handleAlertClose}
            severity="error"
            sx={{ textAlign: "center", width: "70%", borderRadius: 8 }}
          >
            {alertMessage}
          </Alert>
        </Box>
      ) : null}
      <CreateMaterialItemDialog
        materialItem={editableMaterialItem}
        new_main_item_id={new_main_item_id}
        isOpen={open}
        handleClose={handleDialogClose}
        handleSubmit={handleDialogSubmit}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
        proposalId={isExistingProposal ? selectedProposal.id : ""}
      />
      <div
        style={{
          height: materialItems.length ? "auto" : "200px",
          width: "100%",
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="right"
          sx={{ marginBottom: "10px" }}
        >
          {selectedMaterialItems.length ? (
            <Grid item sx={{ marginRight: "4px" }}>
              {loading ? (
                <CircularProgress />
              ) : (
                <ThemedIconButton onClick={handleRemoveMaterialItems}>
                  <RemoveIcon />
                </ThemedIconButton>
              )}
            </Grid>
          ) : null}
          <Grid item sx={{ marginRight: "4px" }}>
            <ThemedIconButton onClick={handleToggleExpandAll}>
              {allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </ThemedIconButton>
          </Grid>
          <Grid item>
            <ThemedIconButton onClick={() => setOpen(true)}>
              <AddIcon />
            </ThemedIconButton>
          </Grid>
        </Grid>
        <MaterialsTable
          gridKey={gridKey}
          rows={materialItems}
          columns={columns}
          loading={materialsLoading}
          getTreeDataPath={getTreeDataPath}
          handleRowSelectionModelChange={handleRowSelectionModelChange}
          apiRef={apiRef}
          projectName={project?.name || 'Project'}
        />
      </div>
    </>
  );
}

export default MaterialItems;
